import { createQueryKeys } from '@lukemorales/query-key-factory';
import { axios } from '@repo/http';
import { navigationItemsKeys, orgListKeys } from '@repo/query-keys/user';
import { organization_path } from '@repo/routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { GET_ORGANIZATION_BY_NAME_PATH, get_organization_by_name_path } from 'ze-api-contract/organization-v2/get-by-name';
import { get_org_name_path } from 'ze-api-contract/organization-v2/settings/change-org-name';
import { GET_ORG_COUNTERS, get_org_counters_path } from 'ze-api-contract/organization-v2/settings/get-counters';
import { get_org_settings_path } from 'ze-api-contract/organization-v2/settings/get-org-settings';
import { GET_USER_PINS_PATH } from 'ze-api-contract/pins/get-pins';
import { GET_ORGANIZATION_LIST_PATH } from 'ze-api-contract/user-v2/get-organization-list';
import { GET_MY_PIN_LIST_PATH } from 'ze-api-contract/user-v2/user-pins/get-my-pin-list';
/** @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md */ export const organizationQuery = createQueryKeys('organization', {
    byName: (organization)=>({
            queryKey: [
                GET_ORGANIZATION_BY_NAME_PATH,
                organization
            ],
            queryFn: async ()=>{
                if (!organization) {
                    throw new Error('Organization name is required');
                }
                return axios.get(get_organization_by_name_path({
                    organization
                })).then((res)=>res.data.value);
            }
        }),
    counters: (organization)=>({
            queryKey: [
                GET_ORG_COUNTERS,
                organization
            ],
            queryFn: async ()=>{
                if (!organization) {
                    return null;
                }
                return axios.get(get_org_counters_path({
                    organization
                })).then((res)=>res.data.value);
            }
        })
});
/**
 * Get organization by organization name.
 *
 * @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md
 */ export function useOrganizationByName(orgName) {
    const { data: organization, isLoading, error } = useQuery(organizationQuery.byName(orgName));
    return {
        organization,
        isLoading,
        error
    };
}
/** @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md */ export function useOrganizationCounters(orgName) {
    const { data: orgCounters, isLoading, error } = useQuery(organizationQuery.counters(orgName));
    return {
        orgCounters,
        isLoading,
        error
    };
}
/** @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md */ export function useUpdateOrganizationPublicSettings(param) {
    let { organization } = param;
    const queryClient = useQueryClient();
    const { queryKey } = organizationQuery.byName(organization);
    return useMutation({
        mutationFn: (val)=>axios.post(get_org_settings_path({
                organization
            }), val),
        onSuccess: async ()=>{
            await queryClient.invalidateQueries({
                queryKey
            });
        }
    });
}
/** @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md */ export function useDeleteOrganization(param) {
    let { organization } = param;
    const queryClient = useQueryClient();
    const { queryKey } = organizationQuery.byName(organization);
    const navigate = useNavigate();
    return useMutation({
        mutationFn: ()=>axios.delete(get_organization_by_name_path({
                organization
            })),
        onSuccess: async ()=>{
            // Invalidate all organization-related queries
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        GET_MY_PIN_LIST_PATH
                    ]
                }),
                queryClient.invalidateQueries({
                    queryKey: [
                        GET_USER_PINS_PATH
                    ]
                }),
                // Also invalidate the organization list query
                queryClient.invalidateQueries({
                    queryKey: [
                        GET_ORGANIZATION_LIST_PATH
                    ]
                }),
                // Force refetch the organization list for current user
                queryClient.resetQueries(orgListKeys.organizationList())
            ]);
            // Navigate to home page which will select an appropriate organization
            void navigate({
                to: '/',
                search: (p)=>p,
                params: (p)=>p
            });
        }
    });
}
/** @deprecated Use ze-sdk equivalents. See docs/migration/DOMAIN_TO_SDK_MAPPING.md */ export function useRenameOrganization(param) {
    let { organization } = param;
    const queryClient = useQueryClient();
    const { queryKey } = organizationQuery.byName(organization);
    const navigate = useNavigate();
    const { queryKey: breadCrumbsOrgList } = navigationItemsKeys.organizationList({
        name: ''
    });
    return useMutation({
        mutationFn: (val)=>axios.post(get_org_name_path({
                organization
            }), val),
        onSuccess: async (res, param)=>{
            let { name } = param;
            await queryClient.invalidateQueries({
                queryKey
            });
            await queryClient.invalidateQueries({
                queryKey: breadCrumbsOrgList
            });
            void navigate({
                to: organization_path({
                    organization: {
                        name
                    }
                })
            });
        }
    });
}
